import * as THREE from 'three';
import { buildingDef, unitDef } from '../content/registry';
import type { Building } from '../sim/state';
import { animateBoss, makeBossRig, newBossAnim } from './bossRigs';
import { BESPOKE_BEAST_DEFS, makeBeastRig, poseBeastRig } from './beastRigs';
import { GameRenderer } from './renderer';
import { makeMountRig, MOUNTED_DEFS, isFlyingMount } from './mountRigs';
import { BESPOKE_WORKER_DEFS, makeWorkerRig, isHoverWorker, poseBarkwrightCircle } from './workerRigs';
import { BESPOKE_VERDANT_SQUAD_DEFS, makeVerdantSquadRig, type VerdantAbilityCue } from './verdantSquadRigs';
import { BESPOKE_IRON_SQUAD_DEFS, makeFortressRamGroup, makeIronSquadRig } from './ironCovenantRigs';
import { isBespokeFactionSquad, makeFactionSquadRig } from './factionSquadRigs';
import {
  OWNER_COLORS,
  STYLES,
  makeArmGeo,
  makeBallistaGroup,
  makeBannerGeo,
  makeBowGeo,
  makeCrossbowGeo,
  makeHatGeo,
  makeHelmDomeGeo,
  makeHelmSpikeGeo,
  makeHeaterShieldGeo,
  makeLegGeo,
  makeRifleGeo,
  makeTankGroup,
  makeTorsoGeo,
  makeTowerShieldGeo,
} from './unitStyles';

export type PreviewMotion = 'idle' | 'walk' | 'charge' | 'attack';

export interface PreviewModel {
  group: THREE.Group;
  animate: (motion: PreviewMotion, time: number, dt: number) => void;
}

function mesh(geometry: THREE.BufferGeometry, color: THREE.Color | number, roughness = 0.8): THREE.Mesh {
  return new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.08, flatShading: true }),
  );
}

function genericMember(defId: string, owner: 0 | 1, seed: number): PreviewModel {
  const d = unitDef(defId);
  const style = STYLES[defId] ?? STYLES['u-ic-vanguard'];
  const group = new THREE.Group();
  const bodyColor = style.bodyColors[owner];
  const limbColor = bodyColor.clone().multiplyScalar(0.55);
  const body = mesh(makeTorsoGeo(), bodyColor);
  group.add(body);
  const head = mesh(new THREE.SphereGeometry(0.24, 10, 8), style.headColor, 0.7);
  head.position.y = 1.62;
  group.add(head);

  for (const [name, x] of [['legL', -0.17], ['legR', 0.17]] as const) {
    const pivot = new THREE.Group();
    pivot.name = name;
    pivot.position.set(x, 0.7, 0);
    pivot.add(mesh(makeLegGeo(), limbColor));
    group.add(pivot);
  }
  const armPivots = new Map<string, THREE.Group>();
  for (const [name, x] of [['armL', -0.4], ['armR', 0.4]] as const) {
    const pivot = new THREE.Group();
    pivot.name = name;
    pivot.position.set(x, 1.32, 0);
    pivot.add(mesh(makeArmGeo(), limbColor));
    group.add(pivot);
    armPivots.set(name, pivot);
  }
  if (style.helm) {
    const geometry = style.helm === 'dome'
      ? makeHelmDomeGeo()
      : style.helm === 'spike'
        ? makeHelmSpikeGeo()
        : makeHatGeo();
    group.add(mesh(geometry, style.helmColor));
  }
  if (style.shield && style.shieldColors) {
    const shield = mesh(style.shield === 'heater' ? makeHeaterShieldGeo() : makeTowerShieldGeo(), style.shieldColors[owner], 0.7);
    shield.position.set(0.42, 1.0, 0.32);
    shield.rotation.y = -0.3;
    group.add(shield);
  }
  if (style.weapon) {
    const weapon = new THREE.Group();
    weapon.name = 'weapon';
    weapon.position.set(0, -0.56, 0.08);
    if (style.weapon.kind === 'bow') {
      weapon.add(mesh(makeBowGeo(), style.weapon.color, 0.8));
      weapon.rotation.z = -0.08;
    } else if (style.weapon.kind === 'crossbow') {
      weapon.add(mesh(makeCrossbowGeo(), style.weapon.color, 0.7));
    } else if (style.weapon.kind === 'rifle') {
      weapon.add(mesh(makeRifleGeo(), style.weapon.color, 0.45));
    } else {
      const shaft = mesh(new THREE.CylinderGeometry(style.weapon.r, style.weapon.r, style.weapon.len, 6), style.weapon.color, 0.55);
      shaft.position.y = style.weapon.len * 0.3;
      weapon.add(shaft);
      weapon.rotation.x = style.weapon.tilt;
    }
    armPivots.get('armL')!.add(weapon);
  }
  if (style.banner) {
    group.add(mesh(makeBannerGeo(), OWNER_COLORS[owner], 0.8));
  }
  group.scale.setScalar(d.ui.scale);
  return {
    group,
    animate: (motion, time) => {
      const moving = motion === 'walk' || motion === 'charge';
      const speed = motion === 'charge' ? 9 : 5;
      const phase = time * speed;
      const stride = moving ? Math.sin(phase) * (motion === 'charge' ? 0.85 : 0.58) : 0;
      group.getObjectByName('legL')!.rotation.x = stride;
      group.getObjectByName('legR')!.rotation.x = -stride;
      const idle = moving ? 0 : 1;
      const idleBreath = Math.sin(time * 1.35 + seed);
      const idleShift = Math.sin(time * 0.62 + seed * 0.7);
      group.getObjectByName('armL')!.rotation.x = motion === 'attack' ? -0.8 * Math.max(0, Math.sin(time * 3)) : -stride * 0.65 + idleBreath * 0.07 * idle;
      group.getObjectByName('armR')!.rotation.x = stride * 0.65 - idleBreath * 0.055 * idle;
      const weapon = group.getObjectByName('weapon');
      if (weapon) {
        const attack = motion === 'attack' ? Math.max(0, Math.sin(time * 3)) : 0;
        weapon.rotation.x = style.weapon?.kind ? -attack * 0.1 : (style.weapon?.tilt ?? 0.5) - attack * 1.1;
      }
      body.rotation.x = motion === 'charge' ? 0.22 : idleBreath * 0.012 * idle;
      body.rotation.z = idleShift * 0.025 * idle;
      head.rotation.y = Math.sin(time * 0.48 + seed) * 0.13 * idle;
      head.rotation.z = idleShift * 0.015 * idle;
      group.position.y = moving ? Math.abs(Math.sin(phase)) * 0.05 * d.ui.scale : idleBreath * 0.026;
    },
  };
}

export function createUnitPreview(defId: string, owner: 0 | 1, seed = 1): PreviewModel {
  const d = unitDef(defId);
  if (d.isBoss) {
    const rig = makeBossRig(defId, owner, d.ui.scale);
    const state = newBossAnim(seed, 0);
    let previousAttackCycle = -1;
    return {
      group: rig.group,
      animate: (motion, time, dt) => {
        const attackCycle = Math.floor(time / 2.2);
        const fired = motion === 'attack' && attackCycle !== previousAttackCycle;
        previousAttackCycle = attackCycle;
        animateBoss(rig, state, {
          t: time,
          dt,
          x: 0,
          z: 0,
          speed: motion === 'charge' ? d.moveSpeed * 1.8 : motion === 'walk' ? d.moveSpeed : 0,
          moveAmt: motion === 'charge' || motion === 'walk' ? 1 : 0,
          winding: motion === 'attack' ? Math.max(0, Math.sin((time % 2.2) / 2.2 * Math.PI)) : 0,
          fired,
          facing: 0,
          targetYaw: null,
          reach: d.attack?.range ?? 1,
          startCrush: false,
        });
      },
    };
  }
  if (BESPOKE_VERDANT_SQUAD_DEFS.has(defId)) {
    const rig = makeVerdantSquadRig(defId, owner, seed - 1, d.ui.scale);
    return {
      group: rig.group,
      animate: (motion, time) => {
        const moving = motion === 'walk' || motion === 'charge';
        const strike = motion === 'attack' ? Math.max(0, Math.sin(time * 2.6)) : 0;
        let ability: VerdantAbilityCue = null;
        if (motion === 'attack' && defId === 'u-vw-sporecallers') ability = seed % 2 === 0 ? 'healing' : 'harmful';
        else if (motion === 'attack' && defId === 'u-vw-sunpetal-seraphs') ability = 'solar';
        else if (motion === 'attack' && defId === 'u-vw-sproutling-swarm') ability = 'healing';
        const phase = time * (motion === 'charge' ? 9 : moving ? 5.2 : 0.55) + seed;
        rig.pose({ time, phase, moveAmt: moving ? 1 : 0, strike, ability });
        rig.group.position.y = defId === 'u-vw-sunpetal-seraphs'
          ? 1.05 * d.ui.scale + Math.sin(time * 1.7 + seed) * 0.08 * d.ui.scale
          : moving ? Math.abs(Math.sin(phase)) * 0.04 * d.ui.scale : 0;
      },
    };
  }
  if (BESPOKE_IRON_SQUAD_DEFS.has(defId)) {
    const rig = makeIronSquadRig(defId, owner, seed - 1, d.ui.scale);
    return {
      group: rig.group,
      animate: (motion, time) => {
        const moving = motion === 'walk' || motion === 'charge';
        const phase = time * (motion === 'charge' ? 8.5 : moving ? 5 : 0.6) + seed;
        rig.pose({
          time,
          phase,
          moveAmt: moving ? 1 : 0,
          strike: motion === 'attack' ? Math.max(0, Math.sin(time * 2.8)) : 0,
        });
        rig.group.position.y = moving ? Math.abs(Math.sin(phase)) * 0.04 * d.ui.scale : 0;
      },
    };
  }
  if (isBespokeFactionSquad(defId)) {
    const rig = makeFactionSquadRig(defId, owner, seed - 1, d.ui.scale);
    return {
      group: rig.group,
      animate: (motion, time) => {
        const moving = motion === 'walk' || motion === 'charge';
        const phase = time * (motion === 'charge' ? 9 : moving ? 5.2 : 0.6) + seed;
        rig.pose({
          time,
          phase,
          moveAmt: moving ? 1 : 0,
          strike: motion === 'attack' ? Math.max(0, Math.sin(time * 2.8)) : 0,
          charge: motion === 'charge' ? 1 : 0,
        });
        rig.group.position.y = moving ? Math.abs(Math.sin(phase)) * 0.035 * d.ui.scale : 0;
      },
    };
  }
  if (BESPOKE_BEAST_DEFS.has(defId)) {
    const group = makeBeastRig(defId, owner, d.ui.scale);
    return {
      group,
      animate: (motion, time) => {
        const moveAmt = motion === 'walk' || motion === 'charge' ? 1 : 0;
        const strike = motion === 'attack' ? Math.max(0, Math.sin(time * 3)) : 0;
        const speed = motion === 'charge' ? 8.5 : motion === 'walk' ? 5.2 : 0.5;
        poseBeastRig(group, { phase: time * speed + seed, moveAmt, strike, charge: motion === 'charge' ? 1 : 0, time });
      },
    };
  }
  if (MOUNTED_DEFS.has(defId)) {
    const group = makeMountRig(defId, owner, d.ui.scale);
    return {
      group,
      animate: (motion, time) => {
        const moving = motion === 'walk' || motion === 'charge';
        const phase = time * (motion === 'charge' ? 9 : 5) + seed;
        const swing = moving ? Math.sin(phase) * (motion === 'charge' ? 0.75 : 0.5) : 0;
        for (const name of ['legFL', 'legBR']) { const leg = group.getObjectByName(name); if (leg) leg.rotation.x = swing; }
        for (const name of ['legFR', 'legBL']) { const leg = group.getObjectByName(name); if (leg) leg.rotation.x = -swing; }
        const flap = 0.35 + Math.sin(time * 8 + seed) * 0.55;
        const wingL = group.getObjectByName('wingL'); if (wingL) wingL.rotation.z = flap;
        const wingR = group.getObjectByName('wingR'); if (wingR) wingR.rotation.z = -flap;
        const tail = group.getObjectByName('tail'); if (tail) tail.rotation.x = Math.sin(time * 3 + seed) * 0.2;
        const arm = group.getObjectByName('riderArm'); if (arm) arm.rotation.x = motion === 'attack' ? -1.2 * Math.max(0, Math.sin(time * 3)) : Math.sin(time * 1.2 + seed) * 0.035 * Number(!moving);
        const rider = group.getObjectByName('rider');
        if (rider) {
          rider.rotation.x = Math.sin(time * 1.4 + seed) * 0.018 * Number(!moving);
          rider.rotation.z = Math.sin(time * 0.7 + seed) * 0.014 * Number(!moving);
        }
        const mountBody = group.getObjectByName('mountBody');
        if (mountBody) mountBody.scale.y = 1 + Math.sin(time * 1.35 + seed) * 0.025 * Number(!moving);
        const mountHead = group.getObjectByName('mountHead');
        if (mountHead) mountHead.rotation.y = Math.sin(time * 0.55 + seed) * 0.1 * Number(!moving);
        group.position.y = isFlyingMount(defId) ? 1.4 + Math.sin(time * 2 + seed) * 0.1 : Math.abs(Math.sin(phase)) * 0.05 * Number(moving);
      },
    };
  }
  if (BESPOKE_WORKER_DEFS.has(defId)) {
    const group = makeWorkerRig(defId, owner);
    group.scale.setScalar(d.ui.scale);
    return {
      group,
      animate: (motion, time) => {
        const moving = motion === 'walk' || motion === 'charge';
        const phase = time * (motion === 'charge' ? 9 : 5) + seed;
        const swing = moving ? Math.sin(phase) * 0.65 : 0;
        if (defId === 'u-vw-barkwright') {
          poseBarkwrightCircle(group, time, phase, moving ? 1 : 0, motion === 'attack');
        }
        const legL = group.getObjectByName('legL'); if (legL) legL.rotation.x = swing;
        const legR = group.getObjectByName('legR'); if (legR) legR.rotation.x = -swing;
        const halo = group.getObjectByName('halo'); if (halo) halo.rotation.y = time * 1.4;
        const idle = Number(!moving);
        const workerBody = group.getObjectByName('workerBody') ?? group.getObjectByName('core');
        if (workerBody) workerBody.scale.y = 1 + Math.sin(time * 1.55 + seed) * 0.035 * idle;
        const workerHead = group.getObjectByName('workerHead');
        if (workerHead) workerHead.rotation.y = Math.sin(time * 0.6 + seed) * 0.18 * idle;
        group.position.y = isHoverWorker(defId) ? 0.45 + Math.sin(time * 2) * 0.08 : Math.abs(Math.sin(phase)) * 0.04 * Number(moving);
      },
    };
  }
  if (defId === 'u-ic-ironclad-tank') {
    const group = makeTankGroup(owner);
    group.scale.setScalar(d.ui.scale * 0.9);
    return { group, animate: (motion, time) => {
      const roll = (motion === 'walk' || motion === 'charge') ? time * (motion === 'charge' ? 10 : 6) : 0;
      for (let i = 0; i < 6; i++) { const wheel = group.getObjectByName(`wheel${i}`); if (wheel) wheel.rotation.x = roll; }
      const turret = group.getObjectByName('turret'); if (turret) turret.rotation.y = Math.sin(time * 0.65) * 0.45;
    } };
  }
  if (defId === 'u-ic-fortress-ram') {
    const group = makeFortressRamGroup(owner);
    group.scale.setScalar(d.ui.scale * 0.72);
    return { group, animate: (motion, time) => {
      const roll = (motion === 'walk' || motion === 'charge') ? time * (motion === 'charge' ? 8 : 5) : 0;
      for (let i = 0; i < 6; i++) { const wheel = group.getObjectByName(`wheel${i}`); if (wheel) wheel.rotation.x = roll; }
      const ram = group.getObjectByName('ram');
      if (ram) ram.position.z = 0.1 + (motion === 'attack' ? Math.max(0, Math.sin(time * 3)) * 0.52 : 0);
    } };
  }
  if (defId === 'u-ic-mobile-ballista') {
    const group = makeBallistaGroup(owner);
    group.scale.setScalar(d.ui.scale);
    return { group, animate: (motion, time) => {
      const roll = (motion === 'walk' || motion === 'charge') ? time * 7 : 0;
      for (let i = 0; i < 4; i++) { const wheel = group.getObjectByName(`wheel${i}`); if (wheel) wheel.rotation.x = roll; }
      const arm = group.getObjectByName('arm'); if (arm) arm.rotation.x = motion === 'attack' ? -0.45 * Math.max(0, Math.sin(time * 3)) : 0;
    } };
  }
  return genericMember(defId, owner, seed);
}

export function createBuildingPreview(defId: string, owner: 0 | 1): PreviewModel {
  const d = buildingDef(defId);
  const building = {
    defId,
    owner,
    pos: { x: 0, y: 0 },
    isCore: d.kind === 'core',
    gateSpan: d.kind === 'wall' ? Math.min(2, Math.max(0, d.footprint.h - 2)) : 0,
    rot: 0,
  } as Building;
  const group = GameRenderer.makeBuildingGroup(building);
  // Show optional model pieces in the inspector; they are part of the production model.
  group.traverse((o) => {
    if (o.name === 'arbalest' || o.name === 'towerArmor' || o.name === 'upgradeDeco' || o.name.startsWith('annex-')) o.visible = true;
  });
  const previewTurret = group.getObjectByName('turret');
  if (previewTurret && defId === 'b-ic-barracks') previewTurret.position.y = 7.05;
  return {
    group,
    animate: (_motion, time) => {
      const turret = group.getObjectByName('turret'); if (turret) turret.rotation.y = time * 0.25;
      const arbalest = group.getObjectByName('arbalest'); if (arbalest) arbalest.rotation.y = Math.sin(time * 0.45) * 0.5;
      const orb = group.getObjectByName('orb'); if (orb) orb.scale.setScalar(1 + Math.sin(time * 2) * 0.06);
      const spinner = group.getObjectByName('spinner'); if (spinner) spinner.rotation.y = time * 0.34;
      const spinnerReverse = group.getObjectByName('spinnerReverse'); if (spinnerReverse) spinnerReverse.rotation.y = -time * 0.23;
      const carriage = group.getObjectByName('assemblyCarriage'); if (carriage) carriage.position.z = Math.sin(time * 0.58) * 1.05;
      const pump = group.getObjectByName('pump'); if (pump) pump.scale.y = 1 + Math.sin(time * 2.2) * 0.035;
      const pulseCore = group.getObjectByName('pulseCore') as THREE.Mesh | null;
      if (pulseCore) {
        const baseScale = pulseCore.userData.baseScale as THREE.Vector3 | undefined;
        const pulse = 1 + Math.sin(time * 2.05) * 0.055;
        if (baseScale) pulseCore.scale.copy(baseScale).multiplyScalar(pulse);
        else pulseCore.scale.setScalar(pulse);
        if (pulseCore.material instanceof THREE.MeshStandardMaterial) pulseCore.material.emissiveIntensity = 1.15 + Math.sin(time * 2.05) * 0.35;
      }
    },
  };
}
